import {
  BANK_TRANSFER_TRACKING_FEATURE_NAME,
  BANK_TRANSFER_TRACKING_SOURCE_TYPE_MYQIMA,
  BANK_TRANSFER_TRACKING_SOURCE_TYPE_PORTAL,
} from '@aca-new/app/pages/payments/shared/constants/bank-transfer-tracking.constants';
import { ANONYMOUS_USER } from '@aca-new/app/shared/constants/app.constants';
import { ISystemEvent } from '@aca-new/app/shared/models/interfaces/system-event.interface';
import { AppSystemEventService } from '@aca-new/app/shared/services/data-track-services/system-event/app-system-event.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

export type TBankTransferSurface = 'myQIMA' | 'payment portal';

export type TBankTransferConfirmMethod = 'tick' | 'transaction_id' | 'both';

export type TBankTransferSubmitResult = 'success' | 'failure';

@Injectable({
  providedIn: 'root',
})
export class BankTransferTrackingService {
  private readonly _appSystemEventService: AppSystemEventService = inject(AppSystemEventService);
  private readonly _appAuthenticationService: AppAuthenticationService = inject(AppAuthenticationService);

  /**
   * @description
   * Tracks opening the bank-transfer option.
   * @param {TBankTransferSurface} surface - myQIMA or payment portal.
   * @param {number} invoiceCount - Number of payment items on the checkout, or 1 on the portal.
   * @param {string} sourceId - First payment item id, portal order id, or empty when unknown.
   * @returns {void} Nothing.
   */
  public trackOpened(surface: TBankTransferSurface, invoiceCount: number, sourceId: string): void {
    this._send('bank_transfer_opened', surface, invoiceCount, sourceId);
  }

  /**
   * @description
   * Tracks a successful copy of the payment reference.
   * @param {TBankTransferSurface} surface - myQIMA or payment portal.
   * @param {number} invoiceCount - Number of payment items on the checkout, or 1 on the portal.
   * @param {string} sourceId - First payment item id, portal order id, or empty when unknown.
   * @returns {void} Nothing.
   */
  public trackReferenceCopied(surface: TBankTransferSurface, invoiceCount: number, sourceId: string): void {
    this._send('reference_copied', surface, invoiceCount, sourceId);
  }

  /**
   * @description
   * Tracks the confirm route after it changes.
   * @param {TBankTransferSurface} surface - myQIMA or payment portal.
   * @param {number} invoiceCount - Number of payment items on the checkout, or 1 on the portal.
   * @param {TBankTransferConfirmMethod} confirmMethod - Tick, transaction id, or both.
   * @param {string} sourceId - First payment item id, portal order id, or empty when unknown.
   * @returns {void} Nothing.
   */
  public trackConfirmMethodChosen(surface: TBankTransferSurface, invoiceCount: number, confirmMethod: TBankTransferConfirmMethod, sourceId: string): void {
    // SP-38062 names this property confirm_method.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    this._send('confirm_method_chosen', surface, invoiceCount, sourceId, { confirm_method: confirmMethod });
  }

  /**
   * @description
   * Tracks the bank-transfer submit outcome.
   * @param {TBankTransferSurface} surface - myQIMA or payment portal.
   * @param {number} invoiceCount - Number of payment items on the checkout, or 1 on the portal.
   * @param {TBankTransferSubmitResult} result - Success or failure.
   * @param {string} sourceId - First payment item id, portal order id, or empty when unknown.
   * @returns {void} Nothing.
   */
  public trackSubmitResult(surface: TBankTransferSurface, invoiceCount: number, result: TBankTransferSubmitResult, sourceId: string): void {
    this._send('bank_transfer_submit_result', surface, invoiceCount, sourceId, { result });
  }

  /**
   * @description
   * Posts one system event. myQIMA uses the signed-in endpoint. The payment portal uses the anonymous endpoint.
   * @param {string} action - Event action from SP-38062.
   * @param {TBankTransferSurface} surface - myQIMA or payment portal.
   * @param {number} invoiceCount - Number of payment items on the checkout, or 1 on the portal.
   * @param {string} sourceId - First payment item id or portal order id.
   * @param {Record<string, string>} extraProperties - Event-specific properties.
   * @returns {void} Nothing.
   */
  private _send(action: string, surface: TBankTransferSurface, invoiceCount: number, sourceId: string, extraProperties: Record<string, string> = {}): void {
    const user = this._appAuthenticationService.getCurrentUser();
    const resolvedSourceId = sourceId || (surface === 'myQIMA' ? user.id || '' : '');

    if (!resolvedSourceId) {
      return;
    }

    const isPortal = surface === 'payment portal';
    const companyId = user.company?.id ?? '';
    const companyName = user.company?.name ?? '';
    const event: ISystemEvent = {
      action,
      featureName: BANK_TRANSFER_TRACKING_FEATURE_NAME,
      sourceId: resolvedSourceId,
      sourceType: isPortal ? BANK_TRANSFER_TRACKING_SOURCE_TYPE_PORTAL : BANK_TRANSFER_TRACKING_SOURCE_TYPE_MYQIMA,
      dataJson: {
        surface,
        // SP-38062 names this property invoice_count.
        // eslint-disable-next-line @typescript-eslint/naming-convention
        invoice_count: invoiceCount,
        ...(companyId ? { companyId } : {}),
        ...(companyName ? { companyName } : {}),
        ...extraProperties,
      },
      ...(isPortal ? { userId: ANONYMOUS_USER, userName: ANONYMOUS_USER } : {}),
    };
    const request$ = isPortal ? this._appSystemEventService.sendSystemEventForAnonymous$(event) : this._appSystemEventService.sendSystemEvent$(event);

    request$.pipe(catchError((): Observable<never> => EMPTY)).subscribe();
  }
}
